
import React, { useState, useMemo } from "react";
import {
  Grid,
  Paper,
  Button,
  TextField,
  FormControlLabel,
  Checkbox,
  Typography,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import notify from "../utils/notifier";
import AdminDashboardService from "../services/AdminDashboardService";

// ============================================================================
// STYLES
// ============================================================================

const useStyles = makeStyles(theme => ({
  root: {
    width: "100%",
    background: "white",
    paddingBottom: theme.spacing(2),
  },
  tableTitleDiv: {
    display: "flex",
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    marginLeft: theme.spacing(1.6),
    flexDirection: 'row',
    fontFamily: 'Times New Roman',
    fontWeight: 'bold'
  },
  checkBox: {
    marginLeft: theme.spacing(1),
    marginTop: theme.spacing(2.5)
  },
  formControl: {
    paddingRight: "0.5rem",
    minWidth: 200,
    maxWidth: 250,
    marginTop: theme.spacing(5),
    paddingBottom: theme.spacing(0),
  },
  textField: {
    paddingRight: theme.spacing(4)
  },
  // Form styles
  formContainer: {
    padding: "20px",
    margin: "16px 0",
    backgroundColor: "#f8f8f8",
    borderRadius: "4px",
    boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
  },
  formTopRow: {
    display: "flex",
    gap: "24px",
    marginBottom: "20px",
    alignItems: "flex-start"
  },
  formLeftColumn: {
    flex: "1 1 45%",
    minWidth: "300px"
  },
  formRightColumn: {
    flex: "1 1 45%",
    minWidth: "300px"
  },
  formButtonContainer: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "12px",
    marginTop: "8px",
    paddingTop: "8px"
  },
  formField: {
    marginBottom: "16px"
  },
  formFieldDescription: {
    marginTop: "0px",
    marginBottom: "16px"
  },
  formFieldValue: {
    marginTop: "0px",
    marginBottom: "16px"
  },
  // Header styles
  headerContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  headerTitle: {
    display: "flex", 
    alignItems: "center"
  },
  // Field type selection styles
  fieldTypeSection: {
    marginBottom: "20px"
  },
  fieldTypeTitle: {
    marginBottom: "12px", 
    fontWeight: "bold"
  },
  // Dialog styles
  dialogContent: {
    marginTop: "16px",
    padding: "12px",
    backgroundColor: "#f5f5f5",
    borderRadius: "4px"
  },
  dialogFieldDetails: {
    fontWeight: "bold", 
    marginBottom: "8px"
  },
  dialogValueContainer: {
    marginTop: "8px",
    padding: "8px",
    backgroundColor: "#fff",
    borderRadius: "4px",
    fontFamily: "monospace",
    fontSize: "0.8rem",
    maxHeight: "150px",
    overflow: "auto",
    border: "1px solid #e0e0e0"
  },
  // Grid styles
  gridItem: {
    minHeight: "87vh"
  }
}));

// ============================================================================
// MAIN COMPONENT
// ============================================================================

/**
 * AddCustomField Component
 * 
 * Provides an interface for adding custom fields to all clients including:
 * - Field type selection (string, boolean, JSON)
 * - Field validation and error handling
 * - Bulk client updates via API
 * - Confirmation dialogs for safety
 * 
 * @returns {JSX.Element} The AddCustomField component
 */

export default function AddCustomField() {
  
  const classes = useStyles();
  // Form Data State
  const [newFieldName, setNewFieldName] = useState("");
  const [newFieldDescription, setNewFieldDescription] = useState("");
  const [newFieldValue, setNewFieldValue] = useState("");
  const [customFieldType, setCustomFieldType] = useState("string");
  
  // UI State
  const [confirmAddDialog, setConfirmAddDialog] = useState(false);
  
  // Validation State
  const [nameError, setNameError] = useState("");
  const [descriptionError, setDescriptionError] = useState("");
  const [valueError, setValueError] = useState("");
  /**
   * Validates field name format and requirements
   * 
   * @param {string} name - Field name to validate
   * @returns {string} Error message or empty string if valid
   */
  const validateFieldName = (name) => {
    if (!name.trim()) {
      return "Field name is required";
    }
    if (name.trim().length < 1) {
      return "Field name must be at least 3 characters";
    }
    if (!/^[a-zA-Z][a-zA-Z0-9_]*$/.test(name.trim())) {
      return "Field name must start with a letter and contain only letters, numbers, and underscores";
    }
    return "";
  };

  /**
   * Validates field description format and requirements
   * 
   * @param {string} description - Field description to validate
   * @returns {string} Error message or empty string if valid
   */
  const validateFieldDescription = (description) => {
    if (!description.trim()) {
      return "Field description is required";
    }
    if (description.trim().length < 3) {
      return "Description must be at least 10 characters";
    }
    return "";
  };

  /**
   * Validates field value based on field type
   * 
   * @param {string} value - Field value to validate
   * @param {string} type - Field type (string, boolean, json)
   * @returns {string} Error message or empty string if valid
   */
  const validateFieldValue = (value, type) => {
    if (!value.trim()) {
      return "Field value is required";
    }
    if (type === "json") {
      try {
        JSON.parse(value);
      } catch (e) {
        return "Invalid JSON format";
      }
    }
    return "";
  };
  /**
   * Determines if the form is valid for submission
   */
  const isFormValid = useMemo(() => {
    return (
      newFieldName !== "" &&
      newFieldDescription !== "" &&
      newFieldValue !== "" &&
      !nameError &&
      !descriptionError &&
      !valueError
    );
  }, [newFieldName, newFieldDescription, newFieldValue, nameError, descriptionError, valueError]);

  /**
   * Determines if the form has any changes for enabling clear button
   */
  const hasChanges = useMemo(() => {
    return (
      newFieldName !== "" ||
      newFieldDescription !== "" ||
      newFieldValue !== ""
    );
  }, [newFieldName, newFieldDescription, newFieldValue]);

  /**
   * Handles field name input changes and validation
   * 
   * @param {string} value - New field name value
   */
  const handleFieldNameChange = (value) => {
    setNewFieldName(value);
    setNameError(validateFieldName(value));
  };

  /**
   * Handles field description input changes and validation
   * 
   * @param {string} value - New field description value
   */
  const handleFieldDescriptionChange = (value) => {
    setNewFieldDescription(value);
    setDescriptionError(validateFieldDescription(value));
  };

  /**
   * Handles field value input changes and validation
   * 
   * @param {string} value - New field value
   */
  const handleFieldValueChange = (value) => {
    setNewFieldValue(value);
    setValueError(validateFieldValue(value, customFieldType));
  };

  /**
   * Handles field type selection changes
   * 
   * @param {string} type - Selected field type
   */
  const handleCustomFieldTypeToggle = (type) => {
    if (customFieldType === type) {
      return;
    } else {
      setCustomFieldType(type);
      setNewFieldValue("");
      setValueError("");
    }
  };

  /**
   * Opens the confirmation dialog for adding the custom field
   */
  const confirmAddField = () => {
    setConfirmAddDialog(true);
  };

  /**
   * Handles the actual API call to add custom field to all clients
   */
  const handleAddCustomField = async () => {
    setConfirmAddDialog(false);
    try {
      // Prepare the payload according to the new API structure
      let processedValue;
      
      if (customFieldType === "json") {
        processedValue = JSON.parse(newFieldValue);
      } else if (customFieldType === "boolean") {
        processedValue = newFieldValue === "true"; // Convert string to actual boolean
      } else {
        processedValue = newFieldValue; // Keep as string for string type
      }

      const payload = {
        description: newFieldDescription,
        value: processedValue
      };

      // Call the API to add custom field to all clients
      // The API expects fieldKey as query parameter and the field data in body
      await AdminDashboardService.updateAllClientsSettings(payload, { fieldKey: newFieldName });
      
      notify({
        type: "success",
        message: "Custom field added to all clients successfully",
      });

      // Clear form after successful addition
      handleClearCustomField();
    } catch (error) {
      console.error("Error adding custom field:", error);
      notify({
        type: "error",
        message: "Error adding custom field: " + (error.response && error.response.data && error.response.data.message ? error.response.data.message : error.message),
      });
    }
  };

  /**
   * Clears all form fields and resets validation state
   */
  const handleClearCustomField = () => {
    setNewFieldName("");
    setNewFieldDescription("");
    setNewFieldValue("");
    setCustomFieldType("string");
    setNameError("");
    setDescriptionError("");
    setValueError("");
  };
  
  return (
    <div>
      <Paper className={classes.root}>
        <Grid item xs={12} className={classes.gridItem}>
          {/* Header */}
          <div className={`${classes.tableTitleDiv} ${classes.headerContainer}`}>
            <div className={classes.headerTitle}>
              <h2>Add Custom Field</h2>
            </div>
          </div>

          {/* Custom Field Form */}
          <div className={classes.formContainer}>

            {/* Field Type Selection */}
            <div className={classes.fieldTypeSection}>
              <Typography variant="subtitle1" className={classes.fieldTypeTitle}>
                Select Field Type:
              </Typography>
              <FormControlLabel
                className={classes.checkBox}
                control={
                  <Checkbox
                    checked={customFieldType === "string"}
                    onChange={() => handleCustomFieldTypeToggle("string")}
                    color="primary"
                  />
                }
                label="String"
              />
              <FormControlLabel
                className={classes.checkBox}
                control={
                  <Checkbox
                    checked={customFieldType === "boolean"}
                    onChange={() => handleCustomFieldTypeToggle("boolean")}
                    color="primary"
                  />
                }
                label="Boolean"
              />
              <FormControlLabel
                className={classes.checkBox}
                control={
                  <Checkbox
                    checked={customFieldType === "json"}
                    onChange={() => handleCustomFieldTypeToggle("json")}
                    color="primary"
                  />
                }
                label="JSON"
              />
            </div>

            {/* Two column layout */}
            <div className={classes.formTopRow}>
              <div className={classes.formLeftColumn}>
                <div className={classes.formField}>
                  <TextField
                    label="Field Name"
                    variant="outlined"
                    fullWidth
                    required
                    value={newFieldName}
                    onChange={(e) => handleFieldNameChange(e.target.value)}
                    error={nameError !== ""}
                    helperText={nameError || " "}
                    placeholder="e.g., customSettings"
                    InputLabelProps={{
                      shrink: true,
                    }}
                    FormHelperTextProps={{
                      style: { minHeight: "20px" }
                    }}
                  />
                </div>
                <div className={classes.formFieldDescription}>
                  <TextField
                    label="Field Description"
                    variant="outlined"
                    fullWidth
                    required
                    multiline
                    rows={5}
                    inputProps={{ maxLength: 500 }}
                    value={newFieldDescription}
                    onChange={(e) => handleFieldDescriptionChange(e.target.value)}
                    error={descriptionError !== ""}
                    helperText={
                      descriptionError || 
                      `${newFieldDescription.length}/500 characters - Describe what this field is used for`
                    }
                    placeholder="Describe the purpose and usage of this custom field..."
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </div>
              </div>
              <div className={classes.formRightColumn}>
                <div className={classes.formFieldValue}>
                  {customFieldType === "boolean" ? (
                    <TextField
                      select
                      label="Boolean Value"
                      variant="outlined"
                      fullWidth
                      required
                      value={newFieldValue}
                      onChange={(e) => handleFieldValueChange(e.target.value)}
                      error={valueError !== ""}
                      helperText={valueError || "Select true or false"}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    >
                      <MenuItem value="true">True</MenuItem>
                      <MenuItem value="false">False</MenuItem>
                    </TextField>
                  ) : (
                    <TextField
                      label={`Field Value (${customFieldType === "json" ? "JSON" : "String"})`}
                      variant="outlined"
                      fullWidth
                      required
                      multiline
                      rows={9}
                      value={newFieldValue}
                      onChange={(e) => handleFieldValueChange(e.target.value)}
                      error={valueError !== ""}
                      helperText={
                        valueError || 
                        (customFieldType === "json" 
                          ? "Enter valid JSON that will be applied to all clients"
                          : "Enter the default value for this field")
                      }
                      placeholder={
                        customFieldType === "json"
                          ? '{\n  "key": "value",\n  "anotherKey": {\n    "nestedKey": "nestedValue"\n  }\n}'
                          : "Enter default string value..."
                      }
                      InputLabelProps={{
                        shrink: true,
                      }}
                      InputProps={{
                        style: { 
                          height: "auto",
                          minHeight: "232px"
                        }
                      }}
                    />
                  )}
                </div>
              </div>
            </div>

            {/* Action buttons */}
            <div className={classes.formButtonContainer}>
              <Button
                color="primary"
                variant="contained"
                onClick={confirmAddField}
                disabled={!isFormValid}
                style={{
                  minWidth: "120px",
                  height: "40px"
                }}
              >
                Add to All Clients
              </Button>
              <Button
                color="primary"
                variant="outlined"
                onClick={handleClearCustomField}
                disabled={!hasChanges}
                style={{
                  minWidth: "100px",
                  height: "40px"
                }}
              >
                Clear
              </Button>
            </div>
          </div>
        </Grid>
      </Paper>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmAddDialog}
        onClose={() => setConfirmAddDialog(false)}
        aria-labelledby="confirm-dialog-title"
        maxWidth="md"
        fullWidth
      >
        <DialogTitle id="confirm-dialog-title">
          Confirm Add Custom Field
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to add this custom field to <strong>ALL clients</strong>? 
            This action will modify all existing client configurations.
          </DialogContentText>
          <div className={classes.dialogContent}>
            <Typography variant="subtitle2" className={classes.dialogFieldDetails}>
              Field Details:
            </Typography>
            <Typography variant="body2"><strong>Name:</strong> {newFieldName}</Typography>
            <Typography variant="body2"><strong>Type:</strong> {customFieldType}</Typography>
            <Typography variant="body2"><strong>Description:</strong> {newFieldDescription}</Typography>
            <div className={classes.dialogValueContainer}>
              <strong>Value:</strong><br />
              {customFieldType === "json" ? (
                <pre>
                  {(() => {
                    try {
                      return JSON.stringify(JSON.parse(newFieldValue || "{}"), null, 2);
                    } catch (error) {
                      return newFieldValue || "Invalid JSON";
                    }
                  })()}
                </pre>
              ) : (
                newFieldValue
              )}
            </div>
          </div>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setConfirmAddDialog(false)} 
            color="default"
          >
            Cancel
          </Button>
          <Button 
            onClick={handleAddCustomField} 
            color="primary" 
            variant="contained"
          >
            Yes, Add to All Clients
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
